/**
 * Types for the generic, manifest-schema-driven settings form.
 *
 * The renderer is fully module-agnostic: everything it shows is derived from a
 * module's JSON `settingsSchema`. Per-field widget selection is inferred from the
 * schema structure and can be overridden declaratively via the `x-widget` key —
 * so module changes are reflected without any frontend code change.
 */

/**
 * Ein einfügbarer Platzhalter-Chip: der Name, der als `{Name}` in den Text wandert, plus – wenn
 * die Quelle eine Auswahl ist – deren mögliche Werte.
 *
 * Die Werte werden als Badges neben dem Chip angezeigt. Ohne sie sieht der Nutzer nur, DASS ein
 * Wert verfügbar ist, aber nicht, was er annehmen kann — bei einem Auswahl-Ausgabeparameter
 * („Arbeitsraum – Ja" / „Arbeitsraum – Nein") ist genau das die entscheidende Information.
 *
 * Absichtlich hier und nicht im Workflow-Modell: es ist der Vertrag des Widgets, und die
 * schema-form-Schicht bleibt damit frei von Workflow-Wissen.
 */
export interface VariableChip {
  name: string;
  /** Mögliche Werte der Quelle (deren `enum`). */
  options?: string[];
  /** Herkunft für den Tooltip, z.B. „Preisermittlung". */
  origin?: string;
}

/** Normalisiert einen `x-variables`-Eintrag auf die Chip-Form. */
export function toVariableChip(entry: string | VariableChip): VariableChip {
  return typeof entry === 'string' ? {name: entry} : entry;
}

/**
 * Ein wählbarer Vorgängerwert im Bindungs-Picker.
 *
 * Absichtlich strukturell und ohne Workflow-Bezug: `UpstreamVariable` aus `workflow.models`
 * erfüllt diese Form, sodass das Widget dieselben Daten bekommt, ohne den Workflow zu kennen.
 */
export interface BindingCandidate {
  /** `${nodeId.outputs.key}` — genau das, was gespeichert wird. */
  ref: string;
  /** Anzeigename der Ausgabe (Port-Titel). */
  name: string;
  /** `<Knoten>.<Name>` — der eindeutige Weg, wenn `name` mehrfach vorkommt. */
  qualifiedName: string;
  /** Quellschritt; die Gruppierung im Picker folgt ihm. */
  nodeName: string;
  /** True, wenn `name` bei mehreren Vorgängern vorkommt — dann zeigt der Picker `qualifiedName`. */
  ambiguous?: boolean;
  /** Mögliche Werte der Quelle (deren `enum`). */
  options?: string[];
  /**
   * JSON-Schema-`type` der Quelle (z.B. `'string'`, `['number','integer','boolean']`). Fehlt er,
   * gilt die Quelle als Joker — kompatibel mit jedem Ziel (siehe `isTypeCompatible`).
   */
  type?: string | string[];
}

/** Die aufgelöste Quelle eines gebundenen Werts — was das Widget read-only anzeigt. */
export interface BindingSource {
  nodeName: string;
  name: string;
  options?: string[];
  /** True, wenn der Knoten oder der Schlüssel nicht (mehr) existiert. */
  dangling: boolean;
}

/** A (subset of) JSON-Schema plus the flowBIC `x-*` extension keys. */
export interface JsonSchema {
  /** Meist ein einzelner String; manche Module (z.B. Wertevergleich) erlauben mehrere Typen. */
  type?: string | string[];
  title?: string;
  description?: string;
  enum?: any[];
  default?: any;
  properties?: Record<string, JsonSchema>;
  items?: JsonSchema;
  required?: string[];
  minimum?: number;
  maximum?: number;

  /** Explicit widget override (wins over structural inference). */
  'x-widget'?: WidgetName | string;
  /** Festwert-Eingabefeld bei `x-widget: 'value-binding'`; sonst entscheidet der Schema-Aufbau. */
  'x-literal-widget'?: WidgetName | string;
  /** Dynamic option source name resolved by SchemaSourceService (e.g. 'foundry-models'). */
  'x-source'?: string;
  /** Map of enum value → display label. */
  'x-enumLabels'?: Record<string, string>;
  /** Label of the "add row" button for object-array widgets. */
  'x-add-label'?: string;
  /** Number of Var chips offered by the formula widget. */
  'x-varCount'?: number;
  /** Settings key whose (numeric) value drives `x-varCount` — resolved at render time (e.g. Formel: 'input_variablen'). */
  'x-varCountSource'?: string;
  /** Placeholder text for text-like widgets. */
  'x-placeholder'?: string;
  /** Text shown by read-only widgets (badges) when no value is set (e.g. 'Alle Dokumentenklassen'). */
  'x-empty-label'?: string;
  /** Input-port schema key whose linked variable names should be offered as insertable {Platzhalter} chips (textarea widget). */
  'x-variableSource'?: string;
  /**
   * Aufgelöste Platzhalter für `x-variableSource`, zur Renderzeit injiziert (nicht Teil des
   * Manifests). Entweder {@link VariableChip}-Objekte oder – abwärtskompatibel – reine Namen.
   */
  'x-variables'?: Array<string | VariableChip>;
  /** Rows of a textarea widget (default 4). */
  'x-rows'?: number;
  /**
   * Row layout of an object-array repeater. `'card'` gives each entry its own card with per-field
   * labels (KI-Anweisung: Ausgabeparameter); omitted keeps the compact single-line row.
   */
  'x-item-layout'?: 'card' | 'row';
  /** Inside a card row: this field gets its own full-width line instead of sharing the field row. */
  'x-full-width'?: boolean;
  /**
   * Item-Property, deren Wert die zugeklappte Karte eines object-array beschriftet (Default: `name`).
   */
  'x-card-title'?: string;
  /**
   * Nur verknüpfbarer Wert: statt eines leeren Freitextfelds steht da, was ohne Verknüpfung gilt.
   * `satisfied` = es gilt ein sinnvoller Standard („Verbunden – Datei des Prüflaufs"), sonst fehlt
   * die Verknüpfung noch. Ausgewertet in `sf-value-binding.component.ts::unbound`.
   */
  'x-unbound'?: { label: string; satisfied?: boolean };
  /**
   * Vorlage für den Kartentitel, fest (`{template}`) oder je Wert einer Eintrags-Property
   * (`{by, templates}`). Ausgewertet in `widget-registry.ts::formatCardTitle`.
   */
  'x-card-title-template'?: { template?: string; by?: string; templates?: { [value: string]: string } };
  /**
   * Item-Property eines object-array, deren Schema von einer Schwester-Property im SELBEN Eintrag
   * abhängt: `{property, cases}` legt die passende Fallschablone über das Basis-Schema (Variable:
   * der Datentyp bestimmt das Wertfeld). Ausgewertet in `schema-field.component.ts::itemPropFor`.
   */
  'x-schema-when'?: { property: string; cases: Record<string, JsonSchema> };
  /**
   * Schwester-Properties, die beim Ändern dieser Property mitgeleert werden. Gehört zu
   * `x-schema-when`: der alte Wert passt nach einem Typwechsel nicht mehr zum neuen Feld.
   */
  'x-resets'?: string[];
  /** Text-like field whose label row offers the "Prompt verbessern" action (AI prompt optimisation). */
  'x-optimizable'?: boolean;
  /**
   * Input-port property that stays wired on the canvas but is NOT listed in the config panel.
   * Distinct from `x-hidden`, which removes the port itself.
   */
  'x-config-hidden'?: boolean;
  /**
   * Gegenstück zu `x-config-hidden`: kein Port am Knoten, aber im Panel pflegbar (KI-Kontext:
   * Kontextdateien werden dort über die Dateiliste ausgewählt statt verdrahtet).
   */
  'x-port-hidden'?: boolean;
  /**
   * Item-Property eines object-array: nur anzeigen, wenn eine Schwester-Property im SELBEN Eintrag
   * den angegebenen Wert hat (KI-Anweisung: „Optionen"/„Als Verzweigung nutzen" nur bei Datentyp
   * „Auswahl"). Ausgewertet in `schema-field.component.ts::itemKeysFor`.
   */
  'x-visible-when'?: { property: string; equals?: any; in?: any[] };
  /**
   * Zur Renderzeit injizierte Kandidatenliste des `value-binding`-Widgets (nicht Teil des
   * Manifests). Nur das Panel kennt die Vorgängerwerte des Knotens; das Manifest erklärt über
   * `x-widget: 'value-binding'` lediglich, DASS ein Wert bindbar ist.
   */
  'x-binding-candidates'?: BindingCandidate[];
  /**
   * Boolean-Item-Property eines object-array, die sich radio-artig verhält: wird sie in einem
   * Eintrag gesetzt, fällt sie in allen anderen. Damit setzt der Editor „nur EIN Ausgabeparameter
   * darf verzweigen" durch, ohne Fehlermeldung. Ausgewertet in
   * `workflow-node-config.component.ts::enforceExclusiveItemFlags`.
   */
  'x-exclusive'?: boolean;
  /**
   * Enum-Werte, die für NEUE Einträge nicht mehr angeboten werden; ein bestehender Wert bleibt aber
   * sichtbar und wählbar (IFC-Bausteine: die Facetten der Altkarten neben den IDS-Facetten `ids_*`).
   */
  'x-enum-legacy'?: any[];
  /**
   * Wert, mit dem ein NEUER Listeneintrag angelegt wird, wenn er von `default` abweicht. `default`
   * bleibt für gespeicherte Einträge ohne den Schlüssel maßgeblich (IFC-Prüfung: Altkarten ohne
   * `facette` sind „eigenschaft", neue Karten starten als `ids_eigenschaft`).
   */
  'x-new-item-default'?: any;
  /** Zu `x-widget: ids-value`: was der Wert ist (`ifc-class`, `pset`, `property`, …) – für Platzhalter. */
  'x-ids-value-kind'?: string;

  [key: string]: any;
}

export type WidgetName =
  | 'input'
  | 'number'
  | 'select'
  | 'status'
  | 'toggle'
  | 'textarea'
  | 'chips'
  | 'multiselect'
  | 'formula'
  | 'badges'
  | 'files'
  | 'object'
  | 'object-array'
  | 'value-binding'
  | 'ids-value'
  | 'ifc-data-type';

/** Emitted per top-level settings property (keeps the existing __settings.<key> contract). */
export interface FieldChange {
  key: string;
  value: any;
}

export interface SelectOption {
  value: any;
  label: string;
}
